<template>
  <div class="main-page-container">
    <b-modal
      id="exportingProgress"
      hide-footer
      hide-header
      size="lg"
      :scrollable="false"
    >
      <ExportProgress />
    </b-modal>

    <!-- update user types  -->
    <b-modal id="updateUserTypes" hide-footer hide-header scrollable>
      <UpdateActionForm @updateUserTypeOrDeleted="refreshUserTypesGrid" />
    </b-modal>

    <!-- move user types  -->
    <b-modal id="moveUserTypes" hide-footer hide-header scrollable>
      <MoveActionForm @updateUserTypeOrDeleted="refreshUserTypesGrid" />
    </b-modal>

    <!-- move user types  -->
    <b-modal
      id="deleteUserTypeModal"
      size="lg"
      class="popover-modal"
      hide-header
      hide-footer
      no-close-on-esc
      :scrollable="true"
      ref="deleteUserTypeModal"
    >
      <DeleteUserType @updateUserTypeOrDeleted="refreshUserTypesGrid" />
    </b-modal>

    <AlertFoundActiveSession />
    <PageHeader className="d-flex align-items-center " :title="title">
      <router-link to="/add-user-type">
        <b-button
          pill
          class="d-flex align-items-center justify-content-center text-center addNewUserType-btn"
          variant="primary"
          size="sm"
        >
          {{ $t("common.add") }}
        </b-button>
      </router-link>
    </PageHeader>

    <section
      class="user-types-table user-types-white-bg-sec"
      style="background-color: white"
    >
      <DxTreeList
        id="userTypesListData"
        :ref="dataGridTreeRefName"
        :data-source="rowsData"
        :show-borders="false"
        :show-column-lines="true"
        :show-row-lines="true"
        :column-auto-width="true"
        :word-wrap-enabled="true"
        :allowColumnResizing="true"
        :row-alternation-enabled="false"
        :two-way-binding-enabled="true"
        :allow-column-reordering="true"
        :expanded-row-keys="expandedRowKeys"
        :selected-row-keys="selectedRowKeys"
        key-expr="id"
        parent-id-expr="parent_id"
        :class="{
          userTypesGridHeightWithAlert: getActiveSessionsCount,
          userTypesGridHeight: !getActiveSessionsCount,
        }"
        @content-ready="contentReady($event)"
        @editor-preparing="onEditorPreparing($event)"
        @data-error-occurred="onDataErrorOccurred($event)"
        :no-data-text="gridEmptyMessage"
        :rtl-enabled="rtlEnabled"
      >
        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />

        <DxHeaderFilter :visible="true" />

        <!-- <DxSelection mode="multiple" /> -->
        <DxColumnChooser
          :enabled="true"
          mode="select"
          :title="$t('common.grid.columnChooser')"
        />
        <DxFilterRow :visible="true" />
        <DxLoadPanel :enabled="true" />

        <!-- start::Toolbar -->
        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem location="before" template="exportButtonTemplate" />
          <DxItem location="before" template="clearAllTemplate" />
        </DxToolbar>
        <!-- end::Toolbar -->

        <!-- start::scrolling -->
        <DxScrolling mode="standard" :use-native="!rtlEnabled" />
        <!-- end::scrolling -->

        <DxColumn
          data-field="name"
          :caption="$t('common.name')"
          :allow-filtering="true"
          :allow-reordering="false"
          :allow-exporting="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
        />

        <DxColumn
          data-field="registrationMethod"
          :caption="$t('userTypes.registrationMethod')"
          :allow-filtering="false"
          :allow-header-filtering="true"
          :filter-operations="allowedOperations"
        />

        <DxColumn
          data-field="gracePeriod"
          :caption="$t('userTypes.gracePeriod')"
          :allow-filtering="true"
          :allow-header-filtering="false"
          :filter-operations="allowedOperations"
        />

        <DxColumn
          :caption="$t('common.grid.actions')"
          :allow-filtering="false"
          :allow-editing="false"
          :allow-reordering="false"
          :visible="true"
          cell-template="action-cell"
          css-class="select-actions select-actionsDAsh"
        />

        <template #action-cell="{ data }">
          <ActionCell
            @updateUserTypeOrDeleted="refreshUserTypesGrid"
            :hasAtLeastOneParent="hasAtLeastOneParent"
            :parentIds="parentIds"
            :UserTypeData="data.data.data"
          />
        </template>

        <template #exportButtonTemplate>
          <DxDropDownButton
            class="export-dropdown"
            :items="buttonData"
            :text="$t('common.grid.export')"
            @item-click="serverSideExport"
          />
        </template>

        <template #columnButtonTemplate>
          <button
            class="custom-columns"
            @click="dataGridTree.showColumnChooser()"
          >
            <span class="custom-text">{{ $t("common.grid.columns") }} </span>
            <span class="custom-icon">
              <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
            </span>
          </button>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols d-flex align-items-center">
            <div class="toolbar-custom-line"></div>
            <Button
              :text="$t('common.grid.clearFilters')"
              @click="clearFilter"
              styling-mode="outlined"
              type="danger"
              class="clear-CustomColums"
            />
          </div>
        </template>
      </DxTreeList>
    </section>
  </div>
</template>

<script>
const dataGridTreeRefName = "dataGrid";
import { mapActions, mapGetters } from "vuex";
import PageHeader from "@/components/page-header";
import { userTypesColumns } from "@/utils/dataSource";
import ActionCell from "@/components/userTypes/ActionCell";
import UpdateActionForm from "@/components/userTypes/UpdateActionForm";
import MoveActionForm from "@/components/userTypes/MoveActionForm";
import DeleteUserType from "@/components/userTypes/DeleteUserType";
import { default as Button } from "devextreme-vue/button";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import AlertFoundActiveSession from "@/components/emergency/AlertFoundActiveSession";
import ExportProgress from "@/components/grid/ExportProgress";

import {
  DxTreeList,
  DxColumn,
  DxColumnChooser,
  DxHeaderFilter,
  DxToolbar,
  DxItem,
  DxFilterRow,
  DxScrolling,
  DxLoadPanel,
  DxStateStoring,
} from "devextreme-vue/tree-list";

export default {
  title: "User Types",
  components: {
    DxTreeList,
    DxColumn,
    DxColumnChooser,
    DxHeaderFilter,
    DxToolbar,
    DxItem,
    Button,
    DxLoadPanel,
    DxFilterRow,
    DxScrolling,
    DxDropDownButton,
    DxStateStoring,
    PageHeader,
    ActionCell,
    UpdateActionForm,
    MoveActionForm,
    DeleteUserType,
    AlertFoundActiveSession,
    ExportProgress,
  },
  name: "UserType",
  data() {
    return {
      // title: "User Types",
      gridEmptyMessage : this.$t('common.grid.loadingData'),
      dataGridTreeRefName,
      buttonData: ["Excel", "CSV", "PDF"],
      expandedRowKeys: [1, 2],
      selectedRowKeys: [1, 29, 42],
      allowedOperations: ["contains"],
      showClearAll: false,
      hasAtLeastOneParent: false,
      allUserTypes: [],
      parentIds: [],
      rtlEnabled: false,
    };
  },
  watch: {
    getExportProgessAppearance(value) {
      value && this.$bvModal.show("exportingProgress");
    },
  },
  computed: {
    ...mapGetters("i18N", ["getLanguage"]),
    ...mapGetters("userType", ["getUserTypeList", "getAllUsersTypes"]),
    ...mapGetters("grid", [
      "getTableHeaders",
      "getSelectedRows",
      "getLoading",
      "getExportProgessAppearance",
    ]),
    ...mapGetters("authfack", ["userDetails", "currentEntity"]),
    ...mapGetters("emergency", ["getActiveSessionsCount"]),
    rowsData() {
      return this.allUserTypes && this.allUserTypes.length > 0
        ? this.allUserTypes
            .map((type) => {
              return {
                id: type.id,
                parent_id: type.parent_id,
                name: type.name,
                registrationMethod: type.pre_registration_method_view
                  ? type.pre_registration_method_view
                  : "-",
                gracePeriod: type.visit_grace_period
                  ? `${type.visit_grace_period} min`
                  : "-",
                data: type,
                created_at: type.created_at,
              };
            })
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        : [];
    },
    userEmail() {
      return this.userDetails && this.userDetails.email;
    },
    dataGridTree() {
      return this.$refs[this.dataGridTreeRefName].instance;
    },
    title() {
      return this.$t("sideNavbarMenu.usersTypes.text");
    },
  },
  methods: {
    ...mapActions("userType", ["fetchUserTypeList", "fetchAllUserType"]),
    ...mapActions("grid", [
      "createTableHeaders",
      "onTableHeader",
      "updateTableHeaders",
      "exportJobBE",
    ]),
    onEditorPreparing(e) {
      if (e.parentType == "filterRow") {
        e.editorOptions.placeholder = this.$t("common.grid.search");
      }
    },
    contentReady(e) {
      let sortedExp = this.getSortedColumns();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAll = true;
      } else {
        this.showClearAll = false;
      }
      this.setColumnChooserPosition();
    },
    getSortedColumns() {
      let allColumns = Array.from(
        Array(this.dataGridTree.columnCount()).keys()
      ).map((index) => this.dataGridTree.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    setColumnChooserPosition() {
      var columnChooserView = this.dataGridTree.getView("columnChooserView");
      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }
      columnChooserView._popupContainer.option("position", {
        of: this.dataGridTree.element(),
        my: this.rtlEnabled ? "right top" : "left top",
        at: this.rtlEnabled ? "right top" : "left top",
        offset: "0 50",
      });
    },
    clearFilter() {
      this.dataGridTree.clearFilter("row");
      this.dataGridTree.clearFilter("header");
      this.dataGridTree.clearSorting();
    },
    fetchTableHeaders() {
      this.onTableHeader({
        url: "/new-user-types",
        headers: userTypesColumns,
      });
    },
    async customLoad() {
      if (this.userEmail !== "lt_techteam@visipoint.com") {
        await this.onTableHeader({
          url: "/new-user-types",
        });
        let value = this.getTableHeaders;
        console.log("customLoad");
        return value;
      }
    },
    customSave(gridState) {
      if (this.userEmail !== "lt_techteam@visipoint.com") {
        gridState.pageIndex = 0;
        gridState.pageSize = 1000000;
        let value = JSON.stringify(gridState);
        value = value.replace(',"selectedRowKeys":[]', "");
        if (!this.getTableHeaders) {
          this.createTableHeaders({
            url: "/new-user-types",
            headers: value,
            filters: "[]",
          });
        } else if (value != JSON.stringify(this.getTableHeaders)) {
          this.updateTableHeaders({
            url: "/new-user-types",
            headers: value,
            filters: "[]",
          });
        }
        console.log(
          "customSave values",
          value != JSON.stringify(this.getTableHeaders)
        );
      }
    },
    serverSideExport(e) {
      const columnNames = this.dataGridTree.getVisibleColumns();
      const columns = [];
      for (let i = 0; i < columnNames.length; i++) {
        if (columnNames[i].caption !== "Actions") {
          columns.push(columnNames[i].caption);
        }
      }
      this.exportJobBE({
        type: e.itemData,
        headers: columns.reduce((accumulator, value) => {
          return { ...accumulator, [value]: value };
        }, {}),
        gridName: "userTypes",
        entity_id: this.currentEntity.id,
        filterBy: {},
        orderBy: {
          sortField: null,
          sortOrder: null,
        },
      });
    },
    refreshUserTypesGrid() {
      this.dataGridTree.beginCustomLoading();
      this.getAllUserTypesData();
    },
    getAllUserTypesData() {
      // let onlyPermittedOneParentWithChidren = false;
      this.fetchAllUserType().then(async (data) => {
        this.gridEmptyMessage = this.$t('common.grid.loadingData');
        // console.log('dataaaa', data);
        await data.data.map((type) => {
          if (!type.parentId) this.parentIds.push(type.id);
        });
        console.log("parentIds", this.parentIds);
        await data.data.map((type) => {
          // this.parentIds.map((parentId)=>{
          //     if(type.parentId && type.parentId != parentId ) this.hasAtLeastOneParent = true;
          // });
          if (!type.isChild) this.hasAtLeastOneParent = true;
          if (!type.parentName) type.parent_id = null;
        });
        
        this.gridEmptyMessage = this.$t('common.grid.noRecordsYet');
        this.allUserTypes = data.data;
        this.dataGridTree.endCustomLoading();
        // console.log('this.allUserTypes', this.allUserTypes);
        console.log("this.hasAtLeastOneParent", this.hasAtLeastOneParent);
      });
    },
    onDataErrorOccurred(e) {
      console.log("onDataErrorOccurred e", e);
      setTimeout(() => {
        let errorRow = document.querySelector(
          ".dx-treelist-headers .dx-error-message"
        );
        errorRow.innerHTML = this.$t("common.notes.anErrorOccurredGrid");
      }, 100);
    },
  },
  created() {
    document.addEventListener("mousedown", (e) => {
      if (e.path) {
        let isColumnChooser = false;
        for (let i = 0; i < e.path.length; i++) {
          if (typeof e.path[i].matches == "function") {
            if (e.path[i].matches(".dx-overlay-content")) {
              isColumnChooser = true;
            }
          }
        }
        if (!isColumnChooser) this.dataGridTree.hideColumnChooser();
      }
    });
  },
  beforeMount() {
    if (this.getLanguage == "ar") {
      this.rtlEnabled = true;
    }
    this.getAllUserTypesData();
  },
};
</script>

<style lang="scss">
.userTypesGridHeight .dx-treelist-container {
  height: calc(100vh - 197px);
}
.userTypesGridHeightWithAlert .dx-treelist-container {
  height: calc(100vh - 243px);
}

div#userTypesListData {
  .dx-treelist-headers .dx-treelist-table .dx-row > td {
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    color: #939393;
  }

  .dx-treelist-rowsview.dx-treelist-nowrap.dx-empty.dx-scrollable.dx-visibility-change-handler.dx-scrollable-both.dx-scrollable-simulated {
    height: 200px;
    .dx-treelist-nodata {
      font-weight: 700;
      font-size: 16px;
      line-height: 19px;
      text-align: center;
      color: #495057;
    }
    .dx-scrollable-scroll-content {
      display: none;
    }
  }

  .dx-treelist-header-panel {
    padding-inline-start: 0 !important;
    padding-inline-end: 0 !important;
  }

  tr.dx-row.dx-column-lines.dx-treelist-filter-row {
    background: white !important;
  }

  .dx-treelist-headers .dx-treelist-content {
    margin-bottom: -3px !important;
  }

  .dx-item-content.dx-list-item-content {
    white-space: normal;
  }
}
</style>
<style lang="scss" scoped>
button.btn.d-flex.align-items-center.justify-content-center.text-center.addNewUserType-btn.btn-primary.btn-sm.rounded-pill {
  margin-inline-start: 40px;
  height: 27px;
  width: 127px;

  font-weight: 400;
  font-size: 14px;
  line-height: 19px;
  text-align: center;
  color: #ffffff;
}

.user-types-table {
  tbody {
    color: #495057;
  }
  .dx-treelist .dx-row > td {
    font-weight: 400;
    font-size: 16px !important;
    line-height: 19px !important;
  }
}
.user-types-white-bg-sec {
  background-color: white;
  padding-inline-start: 0px;
  // padding-top: 25px;
  border-radius: 8px !important;
}
</style>
